import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { paintingAPI } from '../utils/api';
import SEO from '../components/SEO';
import Hero from '../components/Hero';
import Testimonials from '../components/Testimonials';
import NewsletterSignup from '../components/NewsletterSignup';
import config from '../config/config';
import './Home.css';

const Home = () => {
  const [featuredPaintings, setFeaturedPaintings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedPaintings();
  }, []);

  const fetchFeaturedPaintings = async () => {
    try {
      const response = await paintingAPI.getAll({ featured: 'true' });
      setFeaturedPaintings(response.data.slice(0, 6));
    } catch (error) {
      console.error('Error fetching featured paintings:', error);
    } finally {
      setLoading(false);
    }
  };

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Canvaart",
    "url": "https://canvaart.in",
    "description": "Professional canvas painting portfolio and auction platform",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://canvaart.in/gallery?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const artistSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Aanishka Chowdhury",
    "jobTitle": "Canvas Painting Specialist",
    "url": "https://canvaart.in",
    "description": "Professional canvas artist specializing in abstract art, portraits, landscapes, and custom artwork",
    "knowsAbout": ["Abstract Art", "Portrait Painting", "Landscape Art", "Custom Canvas Work"],
    "workLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "IN"
      }
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Canvaart",
    "description": "Professional canvas painting portfolio and auction platform",
    "url": "https://canvaart.in",
    "telephone": "+91-9933558299",
    "email": "help@canvaart.com",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    },
    "priceRange": "$$$",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    }
  };

  return (
    <div className="home">
      <SEO 
        title="Buy Original Canvas Paintings Online India | Canvaart - Hand-Painted Art Gallery & Auctions"
        description="Buy original canvas paintings online in India at Canvaart. Hand-painted abstract art, portraits, landscapes by artist Aanishka Chowdhury. Custom artwork commissions. Art auctions. Free shipping above ₹5000."
        keywords="buy paintings online India, original canvas art for sale, hand painted paintings, abstract art India, custom portrait painting, canvas wall art, living room paintings, art auction India, Aanishka Chowdhury artist, home decor paintings, buy original artwork"
        url="/"
        schema={[homeSchema, artistSchema, localBusinessSchema]}
      />
      
      <Hero />
      
      {/* Featured Paintings Section */}
      <section className="featured-section" id="gallery" aria-label="Featured Artworks">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Featured Artworks</h2>
            <p className="section-subtitle">Explore the latest masterpieces from our collection</p>
          </motion.div>

          {loading ? (
            <div className="spinner" role="status" aria-label="Loading featured paintings"></div>
          ) : (
            <div className="paintings-grid" role="list">
              {featuredPaintings.map((painting, index) => (
                <motion.article
                  key={painting.id}
                  className="painting-card"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  role="listitem"
                  itemScope
                  itemType="https://schema.org/Painting"
                >
                  <div className="painting-imageGal">
                    <img 
                      src={`${config.BASE_API_URL}${painting.images[0]}`} 
                      alt={`${painting.title} - ${painting.category} painting by Aanishka Chowdhury`}
                      loading="lazy"
                      itemProp="image"
                    />
                    <div className="painting-overlay">
                      <Link to={`/painting/${painting.slug || painting.id}`} className="btn-view" aria-label={`View details of ${painting.title}`}>View Details</Link>
                    </div>
                  </div>
                  <div className="painting-info">
                    <h3 itemProp="name">{painting.title}</h3>
                    <p className="painting-category" itemProp="artform">{painting.category}</p>
                    <p className="painting-price" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                      <span itemProp="priceCurrency">INR</span>
                      <span itemProp="price">₹{painting.price}</span>
                    </p>
                  </div>
                </motion.article>
              ))}
            </div>
          )}

          <div className="view-all-btn">
            <Link to="/gallery" className="btn-primary" aria-label="View all paintings in gallery">View All Paintings</Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section" aria-label="About the Artist">
        <div className="container">
          <div className="about-content-home">
            <motion.div
              className="about-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">About the Artist</h2>
              <p>
                Welcome to Canvaart! I'm a passionate canvas painter specializing in creating unique, 
                vibrant artworks that bring life to any space. With years of experience in various 
                painting techniques, I create custom pieces tailored to your vision.
              </p>
              <p>
                Whether you're looking for abstract art, landscapes, portraits, or custom designs, 
                I work closely with clients to ensure every piece tells a story and captures emotion.
              </p>
              <Link to="/about" className="btn-secondary" aria-label="Learn more about the artist">Learn More</Link>
            </motion.div>
            <motion.div
              className="about-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img src="https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600&q=80" alt="Artist Aanishka Chowdhury painting on canvas in art studio" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shop/Buy Paintings Section - SEO optimized for sales */}
      <section className="shop-section" aria-label="Buy Original Canvas Paintings Online">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Buy Original Canvas Paintings Online</h2>
            <p className="section-subtitle">
              Hand-painted original artwork for your home, office, or as a thoughtful gift. 
              Each painting comes with a Certificate of Authenticity.
            </p>
          </motion.div>
          
          <div className="shop-categories">
            <motion.div 
              className="shop-category-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>Abstract Paintings</h3>
              <p>Bold, expressive abstract canvas art for modern interiors.</p>
              <Link to="/gallery?category=Abstract" className="btn-secondary">Shop Abstract Art</Link>
            </motion.div>
            <motion.div 
              className="shop-category-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>Portrait Paintings</h3>
              <p>Custom and ready-made portrait artwork on canvas.</p>
              <Link to="/gallery?category=Portrait" className="btn-secondary">Shop Portraits</Link>
            </motion.div>
            <motion.div 
              className="shop-category-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>Landscape Paintings</h3>
              <p>Serene landscapes and nature scenes for peaceful spaces.</p>
              <Link to="/gallery?category=Landscape" className="btn-secondary">Shop Landscapes</Link>
            </motion.div>
          </div>
          
          <div className="shop-links">
            <Link to="/gallery" className="shop-link">View All Paintings for Sale</Link>
            <Link to="/auctions" className="shop-link">Browse Art Auctions</Link>
            <Link to="/custom-request" className="shop-link">Commission Custom Art</Link>
            <Link to="/about" className="shop-link">About the Artist</Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Custom Request Section */}
      <section className="cta-section" id="custom" aria-label="Custom Artwork Request">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Have a Custom Request?</h2>
            <p>Let's bring your vision to life! Commission a custom painting tailored to your preferences.</p>
            <Link to="/custom-request" className="btn-primary" aria-label="Request custom artwork commission">Request Custom Artwork</Link>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <NewsletterSignup />
    </div>
  );
};

export default Home;
